// Importação de extrato bancário: OFX (padrão dos bancos) ou CSV (planilha exportada pelo banco).
// Tudo é lido no próprio aparelho; nada vai para o servidor além dos lançamentos que a pessoa importar.
import { lerValor } from './moeda';

export type ItemExtrato = { id: string; data: string; desc: string; valor: number; cat: string };

const p2 = (n: number) => String(n).padStart(2, '0');

/** Data em vários formatos: 20261005..., 2026-10-05, 05/10/2026, 05/10/26, 10/05/2026 (EUA, se o dia passar de 12). */
function lerData(s: string): string | null {
  const x = s.trim();
  let m = x.match(/^(\d{4})(\d{2})(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = x.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${p2(+m[2])}-${p2(+m[3])}`;
  m = x.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/);
  if (m) {
    let d = +m[1], mes = +m[2];
    const y = m[3].length === 2 ? 2000 + +m[3] : +m[3];
    if (mes > 12 && d <= 12) [d, mes] = [mes, d];
    if (mes < 1 || mes > 12 || d < 1 || d > 31) return null;
    return `${y}-${p2(mes)}-${p2(d)}`;
  }
  return null;
}

// Categorias por palavras do histórico (a pessoa pode trocar depois, ou pedir para a IA).
const REGRAS: [string, RegExp][] = [
  ['invest', /aplica|invest|tesouro|cdb|lci|lca|poupan|corretora|resgate/i],
  ['mercado', /supermerc|mercado|atacad|assai|carrefour|p[aã]o de a[cç]|hortifr|a[cç]ougue|padaria|walmart|costco|aldi|lidl/i],
  ['transporte', /uber|\b99\b|cabify|posto|combust|gasolin|shell|ipiranga|petrobras|br mania|estacion|ped[aá]gio|sem parar|metr[oô]|[oô]nibus|lyft/i],
  ['saude', /farm[aá]c|droga|droga ?raia|pague menos|hospital|cl[ií]nic|laborat|unimed|amil|bradesco sa[uú]de|hapvida|odonto|pharmacy/i],
  ['casa', /aluguel|condom|energia|enel|copel|cemig|light|celesc|sabesp|sanepar|[aá]gua|\bg[aá]s\b|internet|vivo|claro|\btim\b|\boi\b|net servi|iptu|\brent\b|electric/i],
  ['lazer', /netflix|spotify|prime video|disney|hbo|max\b|ifood|rappi|restaur|lanchon|bar\b|cinema|ingresso|steam|playstation|xbox/i],
  ['educacao', /escola|col[eé]gio|faculd|universid|curso|udemy|alura|livrar|mensalidade|school/i],
  ['trabalho', /das-?simei|das mei|simples nacional|darf|inss|contab|cartorio|cart[oó]rio|oab|crc/i],
];
export const categoriaDe = (desc: string, valor: number) => (valor > 0 && !/resgate/i.test(desc) ? 'entrada' : REGRAS.find(([, r]) => r.test(desc))?.[0] ?? 'outros');

export const hash = (s: string) => { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0; return (h >>> 0).toString(36); };

function lerOFX(txt: string): ItemExtrato[] {
  const out: ItemExtrato[] = [];
  const campo = (bloco: string, nome: string) => bloco.match(new RegExp(`<${nome}>([^<\\r\\n]*)`, 'i'))?.[1]?.trim() ?? '';
  for (const b of txt.split(/<STMTTRN>/i).slice(1)) {
    const bloco = b.split(/<\/STMTTRN>/i)[0];
    const data = lerData(campo(bloco, 'DTPOSTED'));
    const valor = Number(campo(bloco, 'TRNAMT').replace(',', '.'));
    if (!data || !isFinite(valor) || !valor) continue;
    const desc = [campo(bloco, 'NAME'), campo(bloco, 'MEMO')].filter(Boolean).filter((x, i, a) => a.indexOf(x) === i).join(' · ') || 'Lançamento';
    const fitid = campo(bloco, 'FITID');
    out.push({ id: `x:${fitid || hash(data + valor + desc)}`, data, desc, valor, cat: categoriaDe(desc, valor) });
  }
  return out;
}

/** Divide uma linha CSV respeitando aspas. */
function colunas(linha: string, sep: string) {
  const r: string[] = []; let atual = '', aspas = false;
  for (const c of linha) {
    if (c === '"') aspas = !aspas;
    else if (c === sep && !aspas) { r.push(atual); atual = ''; }
    else atual += c;
  }
  r.push(atual);
  return r.map(x => x.trim());
}

function lerCSV(txt: string): ItemExtrato[] {
  const linhas = txt.split(/\r?\n/).filter(l => l.trim());
  if (!linhas.length) return [];
  const amostra = linhas.slice(0, 10).join('\n');
  const sep = [';', ',', '\t'].sort((a, b) => amostra.split(b).length - amostra.split(a).length)[0];
  const tabela = linhas.map(l => colunas(l, sep));
  const n = Math.max(...tabela.map(r => r.length));
  // descobre as colunas: data (mais datas), valor (mais números), histórico (texto mais longo)
  const conta = (f: (s: string) => boolean) => Array.from({ length: n }, (_, i) => tabela.filter(r => r[i] && f(r[i])).length);
  const ehNum = (s: string) => /^[-+(]?\s*(R\$|\$|€|£)?\s*-?[\d.,]+\)?\s*[DC]?$/i.test(s.trim()) && !lerData(s) && isFinite(lerValor(s));
  const cData = conta(s => !!lerData(s));
  const iData = cData.indexOf(Math.max(...cData));
  const cNum = conta(ehNum).map((v, i) => (i === iData ? -1 : v));
  const cab = tabela[0].map(x => x.toLowerCase());
  let iDeb = cab.findIndex(h => /d[eé]bito|sa[ií]da|debit/.test(h));
  let iCred = cab.findIndex(h => /cr[eé]dito|entrada|credit/.test(h));
  const iValor = cab.findIndex(h => /^valor|amount|value|montante/.test(h));
  const iNum = iValor >= 0 ? iValor : cNum.indexOf(Math.max(...cNum));
  if (iValor >= 0) { iDeb = -1; iCred = -1; }
  const tam = Array.from({ length: n }, (_, i) => (i === iData || i === iNum || i === iDeb || i === iCred ? -1 : tabela.reduce((a, r) => a + (r[i]?.length ?? 0), 0)));
  const iDesc = tam.indexOf(Math.max(...tam));
  const out: ItemExtrato[] = [];
  const repet = new Map<string, number>();   // lançamentos iguais no mesmo dia (dois cafés) continuam separados
  for (const r of tabela) {
    const data = r[iData] ? lerData(r[iData]) : null;
    if (!data) continue;
    let valor: number;
    if (iDeb >= 0 || iCred >= 0) {
      const d = iDeb >= 0 ? Math.abs(lerValor(r[iDeb] ?? '')) : NaN, c = iCred >= 0 ? Math.abs(lerValor(r[iCred] ?? '')) : NaN;
      valor = (isFinite(c) && c ? c : 0) - (isFinite(d) && d ? d : 0);
    } else {
      const s = r[iNum] ?? '';
      valor = lerValor(s);
      if (/\(.*\)|\bD\s*$/i.test(s) && valor > 0) valor = -valor;   // (123,45) ou 123,45 D = saída
    }
    if (!isFinite(valor) || !valor) continue;
    const desc = (r[iDesc] ?? '').replace(/\s+/g, ' ').trim() || 'Lançamento';
    if (/^saldo|saldo anterior|saldo do dia|s a l d o/i.test(desc)) continue;
    const base = hash(data + valor.toFixed(2) + desc);
    const k = (repet.get(base) ?? 0) + 1; repet.set(base, k);
    out.push({ id: `x:${base}${k > 1 ? '-' + k : ''}`, data, desc, valor: Math.round(valor * 100) / 100, cat: categoriaDe(desc, valor) });
  }
  return out;
}

/** Lê o extrato (OFX ou CSV) e devolve os lançamentos, sem repetir. */
export function lerExtrato(texto: string): ItemExtrato[] {
  const itens = /<OFX>|<STMTTRN>/i.test(texto) ? lerOFX(texto) : lerCSV(texto);
  const vistos = new Set<string>();
  return itens.filter(i => (vistos.has(i.id) ? false : (vistos.add(i.id), true))).sort((a, b) => b.data.localeCompare(a.data));
}
